"use client";

// src/components/molecules/legal/cookie-banner.tsx
// Bottom-left cookie consent banner with inline "Customize" preferences panel.
// Visibility owned by <CookieConsentProvider> (organism); this molecule is
// purely presentational + handles its own focus/keyboard wiring.

import { useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import { X } from "lucide-react";
import {
  acceptAll,
  type ConsentState,
  rejectAll,
  setConsent,
} from "@/lib/consent";

type CategoryKey = "necessary" | "preferences" | "analytics" | "marketing";

const CATEGORIES: ReadonlyArray<{
  key: CategoryKey;
  label: string;
  description: string;
  locked?: boolean;
}> = [
  {
    key: "necessary",
    label: "Strictly necessary",
    description: "Required for sign-in, security, and core platform functionality.",
    locked: true,
  },
  {
    key: "preferences",
    label: "Preferences",
    description: "Remember settings like language and region.",
  },
  {
    key: "analytics",
    label: "Analytics",
    description: "Help us understand how the site is used so we can improve it.",
  },
  {
    key: "marketing",
    label: "Marketing",
    description: "Personalise communications and measure campaign performance.",
  },
];

type PanelState = { preferences: boolean; analytics: boolean; marketing: boolean };

export type CookieBannerProps = {
  initialConsent: ConsentState | null;
  onDismiss: (state: ConsentState) => void;
  openPreferencesOnMount?: boolean;
};

/** Minimal shape needed to read/write inline CSS: real DOM elements satisfy
 * this, and a plain object with two spies is enough to test it. */
type StyleTarget = {
  style: {
    setProperty(name: string, value: string): void;
    removeProperty(name: string): void;
  };
};

/** The root element also carries attributes: see {@link BANNER_OPEN_ATTR}. */
type RootTarget = StyleTarget & {
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
};

const BANNER_OFFSET_VAR = "--cookie-banner-offset";

/**
 * Set on <html> while the banner is open. On phones the banner spans the full
 * width, and the page's own fixed widgets (the landing CTA bar, the Telegram
 * button) key off this attribute to step aside: lifting them above the banner
 * would only move them into the band where a focus-scrolled control lands.
 */
export const BANNER_OPEN_ATTR = "data-cookie-banner-open";

/**
 * Reserves space for the banner so a browser-driven scroll (e.g. a keyboard
 * focus move) lands above it instead of underneath it (SC 2.4.11).
 */
export function applyCookieBannerOffset(root: RootTarget, body: StyleTarget, heightPx: number): void {
  root.setAttribute(BANNER_OPEN_ATTR, "");
  root.style.setProperty(BANNER_OFFSET_VAR, `${heightPx}px`);
  root.style.setProperty("scroll-padding-bottom", `var(${BANNER_OFFSET_VAR})`);
  body.style.setProperty("padding-bottom", `var(${BANNER_OFFSET_VAR})`);
}

/** Releases the space reserved by {@link applyCookieBannerOffset}. */
export function clearCookieBannerOffset(root: RootTarget, body: StyleTarget): void {
  root.removeAttribute(BANNER_OPEN_ATTR);
  root.style.removeProperty("scroll-padding-bottom");
  body.style.removeProperty("padding-bottom");
  root.style.removeProperty(BANNER_OFFSET_VAR);
}

// A visible focus ring extends past the button box it belongs to; reserving
// a few extra px keeps a ring-wrapped control from touching the banner too.
const FOCUS_RING_MARGIN_PX = 4;

/**
 * The banner's border-box height plus the CSS `bottom` gap Tailwind's
 * `bottom-4` / `sm:bottom-6` applies, plus {@link FOCUS_RING_MARGIN_PX}.
 * The observer callback's own content-box size (its `contentRect` entry) is
 * the *content* box: it misses the 1px border and the bottom gap,
 * undershooting the space that needs to be reserved to clear the fixed
 * banner (18px short at 390, 26px short at 1440 — exactly the border + gap
 * difference between breakpoints).
 */
export function computeCookieBannerOffset(offsetHeightPx: number, bottomGapPx: number): number {
  return offsetHeightPx + bottomGapPx + FOCUS_RING_MARGIN_PX;
}

export function CookieBanner({
  initialConsent,
  onDismiss,
  openPreferencesOnMount = false,
}: CookieBannerProps) {
  const headingId = useId();
  const descriptionId = useId();
  const bannerRef = useRef<HTMLElement | null>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  const [showPreferences, setShowPreferences] = useState<boolean>(openPreferencesOnMount);
  // Opt-out panel: with no prior choice every category starts on, so the visitor
  // switches off what they don't want. Nothing is stored (and no tracker loads)
  // until they press "Save preferences" or "Accept all" — the toggles are a
  // starting position, not a recorded consent.
  const [panel, setPanel] = useState<PanelState>({
    preferences: initialConsent?.preferences ?? true,
    analytics: initialConsent?.analytics ?? true,
    marketing: initialConsent?.marketing ?? true,
  });

  // Capture the previously focused element so we can restore focus on dismiss.
  useEffect(() => {
    lastFocusedRef.current =
      typeof document !== "undefined" ? (document.activeElement as HTMLElement | null) : null;
    return () => {
      const previous = lastFocusedRef.current;
      if (previous && typeof previous.focus === "function") {
        previous.focus({ preventScroll: true });
      }
    };
  }, []);

  // Reserves the banner's own height (measured live, it grows when the
  // preferences panel opens) so a focused control further up the page is
  // never left underneath the fixed banner (SC 2.4.11). Removed on unmount,
  // i.e. as soon as the banner is dismissed.
  useEffect(() => {
    const node = bannerRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    const body = document.body;
    const observer = new ResizeObserver(() => {
      // Read `bottom` inside the callback, not once at mount: the
      // `sm:bottom-6` breakpoint gap can change across a resize, and
      // `getBoundingClientRect().top` can't be used here instead — the
      // slide-in transform is still running on the first callback.
      const bottomGapPx = parseFloat(getComputedStyle(node).bottom) || 0;
      applyCookieBannerOffset(root, body, computeCookieBannerOffset(node.offsetHeight, bottomGapPx));
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
      clearCookieBannerOffset(root, body);
    };
  }, []);

  // Esc closes the preferences panel; if panel is already closed, Esc rejects all.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      if (showPreferences) {
        setShowPreferences(false);
      } else {
        const state = rejectAll();
        onDismiss(state);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showPreferences, onDismiss]);

  function handleAcceptAll() {
    onDismiss(acceptAll());
  }

  function handleRejectAll() {
    onDismiss(rejectAll());
  }

  function handleSavePreferences() {
    const state = setConsent(panel);
    onDismiss(state);
  }

  function togglePanelKey(key: Exclude<CategoryKey, "necessary">) {
    setPanel((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  return (
    <aside
      ref={bannerRef}
      role="region"
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
      // The consent banner is never captured. Without this, autocapture
      // records the click on "Reject all" itself and PostHog flushes that event
      // right after the rejection. Two markers, two mechanisms: the attribute
      // is in the replay `blockSelector`, while autocapture only honours the
      // `ph-no-capture` class (in posthog-js 1.424 the attribute appears
      // nowhere in its autocapture path).
      data-ph-no-capture
      className="ph-no-capture motion-safe:animate-[slide-in-up_220ms_ease-out_both] fixed inset-x-4 bottom-4 z-50 max-w-[420px] rounded-2xl border border-brand-border bg-white text-brand-ink shadow-[0_18px_40px_-12px_rgba(42,0,79,0.25)] sm:inset-x-auto sm:bottom-6 sm:left-6"
    >
      <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
        <div className="flex flex-col gap-1">
          <h2 id={headingId} className="text-sm font-semibold tracking-tight">
            Cookies on Niftipay
          </h2>
          <p id={descriptionId} className="text-xs leading-relaxed text-brand-ink/70">
            We use cookies to keep Niftipay secure and improve your experience.
            Read our{" "}
            <Link
              href="/legal/cookies"
              className="font-medium text-brand underline-offset-4 hover:underline"
            >
              Cookies Policy
            </Link>
            .
          </p>
        </div>
        <button
          type="button"
          aria-label="Reject all and close"
          onClick={handleRejectAll}
          className="-mr-1 -mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-brand-ink/60 transition-colors hover:bg-brand-surface hover:text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
        >
          <X aria-hidden className="size-4" />
        </button>
      </div>

      {showPreferences ? (
        <div className="border-t border-brand-border px-5 py-4">
          <ul className="flex flex-col gap-3" role="list">
            {CATEGORIES.map((category) => {
              const checked =
                category.locked
                  ? true
                  : panel[category.key as Exclude<CategoryKey, "necessary">];
              const switchId = `${headingId}-${category.key}`;
              return (
                <li key={category.key} className="flex items-start gap-3">
                  <button
                    id={switchId}
                    type="button"
                    role="switch"
                    aria-checked={checked}
                    aria-label={`${category.label} cookies`}
                    disabled={category.locked}
                    onClick={() =>
                      !category.locked &&
                      togglePanelKey(category.key as Exclude<CategoryKey, "necessary">)
                    }
                    className="relative -my-2.5 inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    <span
                      aria-hidden
                      className={`relative inline-flex h-6 w-11 items-center rounded-full border transition-colors ${
                        checked ? "border-brand-border bg-brand" : "border-brand-ink/50 bg-brand-surface"
                      }`}
                    >
                      <span
                        aria-hidden
                        className={`inline-block size-5 transform rounded-full bg-white shadow-sm transition-transform ${
                          checked ? "translate-x-5" : "translate-x-0"
                        }`}
                      />
                    </span>
                  </button>
                  <div className="flex flex-col gap-0.5">
                    <label
                      htmlFor={switchId}
                      className="text-xs font-semibold text-brand-ink"
                    >
                      {category.label}
                      {category.locked ? (
                        <span className="ml-2 text-[10px] font-normal uppercase tracking-wide text-brand-ink/60">
                          Always active
                        </span>
                      ) : null}
                    </label>
                    <p className="text-[11px] leading-snug text-brand-ink/65">
                      {category.description}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      <div className="flex flex-col gap-2 px-5 pb-5 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={() => setShowPreferences((value) => !value)}
          aria-expanded={showPreferences}
          className="inline-flex min-h-11 items-center justify-center rounded-md px-3 text-xs font-medium text-brand-ink/80 underline underline-offset-4 transition-colors hover:text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
        >
          {showPreferences ? "Hide preferences" : "Customize"}
        </button>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {showPreferences ? (
            <button
              type="button"
              onClick={handleSavePreferences}
              className="inline-flex min-h-11 items-center justify-center rounded-md border border-brand-border bg-white px-4 text-xs font-semibold text-brand-ink transition-colors hover:bg-brand-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
            >
              Save preferences
            </button>
          ) : (
            <button
              type="button"
              onClick={handleRejectAll}
              className="inline-flex min-h-11 items-center justify-center rounded-md border border-brand-border bg-white px-4 text-xs font-semibold text-brand-ink transition-colors hover:bg-brand-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
            >
              Reject all
            </button>
          )}
          <button
            type="button"
            onClick={handleAcceptAll}
            className="inline-flex min-h-11 items-center justify-center rounded-md bg-brand px-4 text-xs font-semibold text-white transition-colors hover:bg-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
          >
            Accept all
          </button>
        </div>
      </div>
    </aside>
  );
}
